/**
 * MBA Lab — pure ROI/breakeven engine for the ROI calculator. No DOM, storage,
 * analytics, formatting, or chart code lives here - see roi.js for all of
 * that. Every number returned is exact/undisplayed; rounding for display
 * ("~$X", nearest $500) happens in the UI layer, never here, so the chart can
 * always plot true values.
 *
 * Model: two monthly pre-tax income streams over a 30-year (360-month) grid
 * from enrollment - Scenario A (no MBA) and Scenario B (the MBA path).
 * Nominal dollars throughout (v1 does not model taxes - a symmetric
 * pre-tax comparison, stated in the page's own methodology copy). The
 * cumulative delta between the two streams is a *discounted* running sum
 * (monthly-compounded at `discountRate`), so breakeven answers "worth it in
 * today's-dollars terms", not just nominal dollars; setting discountRate to 0
 * reduces it to a plain nominal cumulative sum.
 */

export const GRID_MONTHS = 360; // 30-year grid, month 0 = enrollment

export const DEFAULTS = Object.freeze({
  currentSalary: 75000,
  noMbaRaiseRate: 0.03,
  totalCost: 0,
  scholarships: 0,
  lengthMonths: 21,
  incomeDuringProgram: 0,
  percentBorrowed: 0.5,
  loanRate: 0.08,
  loanTermYears: 10,
  postMbaRaiseRate: 0.05,
  discountRate: 0.05,
  // postMbaSalary has no fixed default - it resolves to currentSalary (no
  // assumed change) when missing, computed in normalizeInputs.
});

export const LIMITS = Object.freeze({
  percentBorrowed: [0, 1],
  loanRate: [0, 0.25],
  loanTermYears: [1, 30],
  lengthMonths: [1, 120],
  raiseRate: [0, 0.15],
  discountRate: [0, 0.2],
});

const SENSITIVITY_MULTIPLIERS = [0.8, 1, 1.2];

function nonNegativeOr(value, fallback) {
  if (!Number.isFinite(value)) return fallback;
  return value < 0 ? 0 : value;
}

function rangeOr(value, fallback, [min, max]) {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

export function intRangeOr(value, fallback, [min, max]) {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * Clamps/defaults every field so the rest of the engine never sees a
 * non-finite or out-of-range value. Non-finite or missing -> the documented
 * default; finite but out of range -> clamped to the nearest bound (so a
 * negative salary floors at 0 rather than resetting to the $75k default).
 */
export function normalizeInputs(raw) {
  const input = raw && typeof raw === 'object' ? raw : {};

  const currentSalary = nonNegativeOr(input.currentSalary, DEFAULTS.currentSalary);
  const noMbaRaiseRate = rangeOr(input.noMbaRaiseRate, DEFAULTS.noMbaRaiseRate, LIMITS.raiseRate);

  const totalCost = nonNegativeOr(input.totalCost, DEFAULTS.totalCost);
  const scholarships = nonNegativeOr(input.scholarships, DEFAULTS.scholarships);
  const netCost = Math.max(0, totalCost - scholarships);

  const lengthMonths = intRangeOr(input.lengthMonths, DEFAULTS.lengthMonths, LIMITS.lengthMonths);
  const incomeDuringProgram = nonNegativeOr(input.incomeDuringProgram, DEFAULTS.incomeDuringProgram);

  const percentBorrowed = rangeOr(input.percentBorrowed, DEFAULTS.percentBorrowed, LIMITS.percentBorrowed);
  const loanRate = rangeOr(input.loanRate, DEFAULTS.loanRate, LIMITS.loanRate);
  const loanTermYears = intRangeOr(input.loanTermYears, DEFAULTS.loanTermYears, LIMITS.loanTermYears);

  const postMbaSalary = nonNegativeOr(input.postMbaSalary, currentSalary);
  const postMbaRaiseRate = rangeOr(input.postMbaRaiseRate, DEFAULTS.postMbaRaiseRate, LIMITS.raiseRate);
  const discountRate = rangeOr(input.discountRate, DEFAULTS.discountRate, LIMITS.discountRate);

  return {
    currentSalary,
    noMbaRaiseRate,
    totalCost,
    scholarships,
    netCost,
    lengthMonths,
    incomeDuringProgram,
    percentBorrowed,
    loanRate,
    loanTermYears,
    postMbaSalary,
    postMbaRaiseRate,
    discountRate,
  };
}

/**
 * Standard fixed-payment amortization. principal <= 0 -> 0 (nothing to repay,
 * never a 0/0 from an also-zero rate). rate === 0 -> straight-line
 * principal/n (the standard formula divides by zero at i=0). annualRate/
 * termYears must be finite - callers (normalizeInputs) guarantee this in
 * practice, but a non-finite termYears would otherwise poison n's <= 0 guard
 * (NaN <= 0 is false) and propagate NaN through the whole payment.
 */
export function monthlyLoanPayment(principal, annualRate, termYears) {
  if (!Number.isFinite(annualRate) || !Number.isFinite(termYears)) return 0;
  const n = 12 * termYears;
  if (!(principal > 0) || n <= 0) return 0;
  const i = annualRate / 12;
  if (i === 0) return principal / n;
  return (principal * i) / (1 - Math.pow(1 + i, -n));
}

function simulate(n) {
  const principal = n.percentBorrowed * n.netCost;
  const selfFundedTotal = (1 - n.percentBorrowed) * n.netCost;
  const selfFundedPerMonth = n.lengthMonths > 0 ? selfFundedTotal / n.lengthMonths : 0;
  const incomeDuringProgramPerMonth = n.lengthMonths > 0 ? n.incomeDuringProgram / n.lengthMonths : 0;
  const loanPayment = monthlyLoanPayment(principal, n.loanRate, n.loanTermYears);
  const loanTermMonths = 12 * n.loanTermYears;
  const monthlyLoanRate = n.loanRate / 12;

  const months = new Array(GRID_MONTHS);
  let loanPaymentsInGrid = 0;
  let loanBalance = principal;
  let loanInterestPaid = 0;
  for (let m = 0; m < GRID_MONTHS; m++) {
    const scenarioA = (n.currentSalary / 12) * Math.pow(1 + n.noMbaRaiseRate, Math.floor(m / 12));

    let scenarioB;
    if (m < n.lengthMonths) {
      scenarioB = incomeDuringProgramPerMonth - selfFundedPerMonth;
    } else {
      const monthsSinceGraduation = m - n.lengthMonths;
      const postSalaryMonthly =
        (n.postMbaSalary / 12) * Math.pow(1 + n.postMbaRaiseRate, Math.floor(monthsSinceGraduation / 12));
      // The loan amortizes over loanTermYears from the first post-graduation
      // payment - stop subtracting it once paid off rather than treating
      // "post phase" as one undifferentiated span for the rest of the grid.
      const isPaymentActive = monthsSinceGraduation < loanTermMonths;
      if (isPaymentActive) {
        loanPaymentsInGrid++;
        // Track true interest (this month's balance * rate) via a running
        // balance rather than deriving it later as (payments - principal) -
        // that shortcut is only valid when the loan is fully repaid within
        // the payments counted, and undercounts (or even floors to $0, see
        // computeCostCards) whenever loanTermYears extends past month 359
        // and a real balance is still outstanding at the grid's edge.
        const interestThisMonth = loanBalance * monthlyLoanRate;
        loanInterestPaid += interestThisMonth;
        loanBalance = Math.max(0, loanBalance - (loanPayment - interestThisMonth));
      }
      scenarioB = postSalaryMonthly - (isPaymentActive ? loanPayment : 0);
    }

    months[m] = { m, scenarioA, scenarioB, delta: scenarioB - scenarioA };
  }

  return { months, principal, loanPayment, incomeDuringProgramPerMonth, loanPaymentsInGrid, loanInterestPaid };
}

function withCumulativeDiscounted(months, discountRate) {
  const monthlyDiscount = discountRate / 12;
  let cumulative = 0;
  return months.map((row) => {
    const discountFactor = Math.pow(1 + monthlyDiscount, row.m);
    cumulative += row.delta / discountFactor;
    return { ...row, cumulativeDiscounted: cumulative };
  });
}

function roundToHalfYear(years) {
  return Math.round(years * 2) / 2;
}

/**
 * Payback semantics: breakeven is the month *after* the cumulative curve is
 * negative for the LAST time - the point past which the MBA path stays ahead
 * for the rest of the grid - not merely the first moment the curve touches
 * zero. Those differ in two real, user-reachable ways:
 *  - A student whose entered income during the program happens to match
 *    their no-MBA-path earnings, financing 100% of tuition, has delta === 0
 *    for the entire program phase (nothing is out of pocket yet because it's
 *    all borrowed, and the entered income offsets what they'd have earned
 *    anyway), so month 0's cumulative is *exactly* 0 while the real cost
 *    only lands once loan repayment starts after graduation. Reading month 0
 *    as "already ahead" reported "net positive from the start" on a path that
 *    then spent 15+ years underwater by up to $53k. (Originally found via a
 *    part-time/online/executive format, back when the engine hardcoded that
 *    same income-matches-earnings assumption per format instead of reading
 *    it from an editable field - the condition itself was never actually
 *    about format.)
 *  - A curve that crosses zero, dips back under, then recovers would
 *    otherwise report that first temporary crossing as the payback point.
 * status: "immediate" (never negative anywhere in the grid), "reached"
 * (recovers at `month` and stays non-negative through month 359), or "none"
 * (still negative at the end of the 30-year grid).
 */
function findBreakeven(monthsWithCumulative) {
  let lastNegativeMonth = -1;
  for (const row of monthsWithCumulative) {
    if (row.cumulativeDiscounted < 0) lastNegativeMonth = row.m;
  }

  if (lastNegativeMonth === -1) return { status: 'immediate', month: 0, years: 0 };
  if (lastNegativeMonth >= monthsWithCumulative.length - 1) {
    return { status: 'none', month: null, years: null };
  }

  const month = lastNegativeMonth + 1;
  return { status: 'reached', month, years: roundToHalfYear(month / 12) };
}

function computeCostCards(n, sim) {
  const netProgramCost = n.netCost;
  // Interest actually embedded in the payments captured inside the 360-month
  // grid the chart/breakeven plot shows - tracked per-month in simulate() via
  // a running balance, not derived as (totalPayments - originalPrincipal).
  // That shortcut is only valid once the loan is fully repaid within the
  // payments counted; a loan term extending past month 359 leaves a real
  // balance outstanding at the cutoff, which the shortcut ignores entirely -
  // undercounting interest, or even flooring it to $0, on a still-unpaid loan.
  const financingInterest = Math.max(0, sim.loanInterestPaid);

  // Computed uniformly for every format now - it's just "what you'd have
  // earned on the no-MBA path minus what you actually entered as income
  // during the program," whatever that entered figure is (see
  // defaultIncomeDuringProgram() in roi-view.js for how it defaults per
  // format; the user can always override it directly).
  let foregoneEarnings = 0;
  for (let m = 0; m < n.lengthMonths; m++) {
    foregoneEarnings += sim.months[m].scenarioA - sim.incomeDuringProgramPerMonth;
  }
  // Floor at 0, matching financingInterest above - entered income during the
  // program can legally exceed the salary given up (e.g. a well-paid
  // part-time role), and this card represents a cost, never a negative one.
  foregoneEarnings = Math.max(0, foregoneEarnings);

  return {
    netProgramCost,
    financingInterest,
    foregoneEarnings,
    totalEconomicInvestment: netProgramCost + financingInterest + foregoneEarnings,
  };
}

function computeSensitivity(n) {
  return SENSITIVITY_MULTIPLIERS.map((multiplier) => {
    const scaled = { ...n, postMbaSalary: n.postMbaSalary * multiplier };
    const sim = simulate(scaled);
    const withCumulative = withCumulativeDiscounted(sim.months, scaled.discountRate);
    return { multiplier, breakeven: findBreakeven(withCumulative) };
  });
}

/**
 * @param {object} rawInputs - see normalizeInputs()/DEFAULTS for the accepted
 *   shape; any missing/invalid field falls back to a safe default or clamp.
 * @returns {{
 *   inputs: object,
 *   months: Array<{m:number, scenarioA:number, scenarioB:number, delta:number, cumulativeDiscounted:number}>,
 *   principal: number,
 *   loanMonthlyPayment: number,
 *   netProgramCost: number,
 *   financingInterest: number,
 *   foregoneEarnings: number,
 *   totalEconomicInvestment: number,
 *   breakeven: {status: 'immediate'|'reached'|'none', month: number|null, years: number|null},
 *   sensitivity: Array<{multiplier:number, breakeven: object}>,
 * }}
 */
export function computeRoi(rawInputs) {
  const n = normalizeInputs(rawInputs);
  const sim = simulate(n);
  const months = withCumulativeDiscounted(sim.months, n.discountRate);
  const breakeven = findBreakeven(months);
  const costCards = computeCostCards(n, sim);
  const sensitivity = computeSensitivity(n);

  return {
    inputs: n,
    months,
    principal: sim.principal,
    loanMonthlyPayment: sim.loanPayment,
    breakeven,
    sensitivity,
    ...costCards,
  };
}
